"use client";

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar, type SnackbarKey } from "notistack";
import { useAuth } from "@/src/core/auth/useAuth";
import { errorMessage } from "@/src/core/api/validation";
import { wishlistKeys } from "../api/queries";
import {
  WISHLIST_ERROR,
  WISHLIST_TOAST as TOAST,
  wishlistService,
} from "../services";
import { wishlistActions, wishlistStore } from "../store/wishlistStore";

/**
 * One heart's state and tap, the app's `WishlistHeartButton._onTap`:
 *
 * - signed out → the login modal;
 * - saved in several folders → the save sheet (each folder can be unticked there);
 * - saved in one folder → removed from it, "Removed from X";
 * - not saved, with a default folder → saved there, "Saved to X · Change";
 * - not saved, no default → the save sheet (the create sheet when there are no folders).
 *
 * The heart flips only after the server confirms; until then it spins.
 */
export const useWishlistHeart = (listingId: number) => {
  const { isAuthenticated, id: userId, login } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { savedIn, favourites, pending } = wishlistStore.useStore();

  // The first heart on a page loads what the user has saved (once per session).
  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    wishlistActions.claim(Number(userId));
    void wishlistActions.hydrate();
  }, [isAuthenticated, userId]);

  const toast = (message: string, isError = false) =>
    enqueueSnackbar(message, {
      ...TOAST,
      variant: isError ? "error" : "default",
    });

  const toggle = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }
    if (wishlistStore.get().pending.includes(listingId)) return;

    await wishlistActions.hydrate();
    const { hydrated, folders, savedIn: current } = wishlistStore.get();
    if (!hydrated) {
      toast(WISHLIST_ERROR, true);
      return;
    }

    const inFolders = current[listingId] ?? [];
    try {
      if (inFolders.length > 1) {
        wishlistActions.openSheet({ type: "save", listingId });
        return;
      }

      if (inFolders.length === 1) {
        const name = folders.find((f) => f.id === inFolders[0])?.name ?? "";
        await wishlistActions.remove(listingId);
        void queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
        toast(`Removed from ${name}`);
        return;
      }

      const defaultFolder = wishlistService.defaultFolder(folders);
      if (!defaultFolder) {
        wishlistActions.openSheet(
          folders.length
            ? { type: "save", listingId }
            : { type: "create", listingId },
        );
        return;
      }

      await wishlistActions.save(listingId, defaultFolder.id);
      void queryClient.invalidateQueries({ queryKey: wishlistKeys.all });
      enqueueSnackbar(`Saved to ${defaultFolder.name}`, {
        ...TOAST,
        action: (key: SnackbarKey) => (
          <button
            type="button"
            className="ml-2 font-semibold text-white underline underline-offset-2"
            onClick={() => {
              closeSnackbar(key);
              wishlistActions.openSheet({
                type: "save",
                listingId,
                moveFrom: defaultFolder.id,
              });
            }}
          >
            Change
          </button>
        ),
      });
    } catch (error) {
      toast(errorMessage(error, WISHLIST_ERROR), true);
    }
  };

  return {
    isSaved: wishlistService.isSaved(savedIn, favourites, listingId),
    isPending: pending.includes(listingId),
    toggle,
  };
};
